import { Alert, Button, Conditional, If } from '@consumidor-positivo/aurora';
import PaymentSection from './components/PaymentSection';
import { DebtData } from '@common/hooks/useDebts/types';
import { ClosedAgreementProps } from '../../types';
import OrientationCard from '../OrientationCard';
import UpdateContactData from '../UpdateContactData';
import CardSection from './components/CardSection';
import HotjarSurvey from '@common/components/HotjarSurvey';

import { GenerateBilletResponse } from '@common/apis/billet/types';
import useAuthenticatedUser from '@libs/acp/hooks/useAuthenticatedUser';
import { UpdateEmailModal } from '@libs/acp/components/exported/UpdateEmailModal';
import { PixPaymentConfirmationAlert } from '@libs/renegkit/components/common/alerts/PixPaymentConfirmation';
import { useUpdateEmailModal } from './hooks/useUpdateEmailModal';
import * as nubank from '@dues/helpers/nubank';
import * as bradesco from '@dues/helpers/bradesco';

import './styles.scss';

interface PaymentDataProps {
  handleSeeAgreementDetails: () => void;
  isFirstInstallmentPaid: boolean;
  closedAgreement: ClosedAgreementProps;
  debtData: DebtData;
  hasBillet: boolean;
  pixCode: string;
  isPixExternal: boolean;
  billet: GenerateBilletResponse | null;
  isPixSuccess: boolean;
  qrCodeUrl: string;
  handleCopyPixCode: () => void;
  pixText: string;
  firstInstallmentExpDate: string;
  warningRef: React.MutableRefObject<HTMLElement | null>;
  isPollingBillet: boolean;
  handleUpdateContactData: () => void;
  handleRedirectToMyDebts: () => void;
  isLoadingCAPix: boolean;
  isItauDesenrola?: boolean;
}

const PaymentData = ({
  handleSeeAgreementDetails,
  isFirstInstallmentPaid,
  closedAgreement,
  debtData,
  warningRef,
  hasBillet,
  pixCode,
  isPixExternal,
  billet,
  qrCodeUrl,
  handleCopyPixCode,
  pixText,
  firstInstallmentExpDate,
  isPollingBillet,
  handleUpdateContactData,
  isLoadingCAPix,
  isItauDesenrola,
}: PaymentDataProps) => {
  const showPixErrorWarning = !!closedAgreement?.flowWithPix && !pixCode && !isPixExternal && hasBillet && !isLoadingCAPix;

  const { authenticatedUser, tokenOrigin } = useAuthenticatedUser();
  const isNCADCheckout = authenticatedUser?.customer?.required_updates?.includes('account_ncad');
  const isSimplified = tokenOrigin() === 'simplified';

  const {
    email: userEmail,
    isNeverEngaged,
    isModalOpen,
    showEmailAlert,
    updatedEmail,
    handleEmailUpdate,
    handleModalOpen,
    handleModalClose,
  } = useUpdateEmailModal();

  const showUpdateContactAlert = !showPixErrorWarning && !isFirstInstallmentPaid && !isNCADCheckout && !isNeverEngaged;
  const showNeverEngagedEmailAlert = showEmailAlert && isNeverEngaged && !closedAgreement?.debitAccount?.debitInAccountRequired;
  const isOnlyPix = debtData?.paymentMeth?.id === 'PIX' && !hasBillet && isPixExternal;
  const isPixBillet = !!closedAgreement?.flowWithPix && !closedAgreement?.singlePaymentMethod && !isPixExternal;
  const showOrientationCard = !hasBillet && !pixCode && !isPollingBillet;
  const isBradescoDesenrola = bradesco.isDesenrolaBradesco(closedAgreement?.debt);

  const hasNewNubankDueDate = nubank.checkNubankLoansDueDate({
    product: debtData.display.product,
    isNubank: debtData.currentPartner.isNubank,
    hasBillet,
    agreementDate: closedAgreement?.agreement?.dataAcordo,
    firstInstallmentExpDate,
    shouldFormatDate: true,
  });

  const getNubankLoansMessage = () => {
    if (hasNewNubankDueDate) {
      const [day, month] = firstInstallmentExpDate.split('/');
      return `Seu boleto ficará disponível no dia <span>${day}/${month} via e-mail.</span><br /> Agora é só aguardar!`;
    }
    return '';
  };

  return (
    <>
      <Conditional
        condition={showPixErrorWarning}
        renderIf={
          <div className="closed-agreement-page__alert">
            <Alert
              status="error"
              title={{
                content:
                  'Tivemos um problema ao gerar seu código Pix. Aguarde até 24h para receber o código via e-mail ou faça o pagamento via boleto.',
              }}
              type={2}
            />
          </div>
        }
      />
      <Conditional
        condition={showUpdateContactAlert}
        renderIf={
          <div className="closed-agreement-page__alert">
            <Alert
              actionButton={{
                content: 'Atualizar dados',
                onClick: handleUpdateContactData,
              }}
              status="warning"
              title={{
                content: 'Atualize seus dados para receber avisos de parcelas e vencimentos.',
              }}
              type={2}
            />
          </div>
        }
      />
      <Conditional
        condition={showNeverEngagedEmailAlert}
        renderIf={
          <div className="closed-agreement-page__alert">
            <Alert status="info" type={2}>
              Você receberá os boletos das próximas parcelas no seguinte e-mail:
              <br />
              <div className="info-alert__email">{updatedEmail || userEmail}</div>
              <Button className="info-alert__button" type="link" onClick={handleModalOpen}>
                Alterar e-mail
              </Button>
            </Alert>
          </div>
        }
      />

      <If condition={hasNewNubankDueDate}>
        <div className="closed-agreement-page__alert">
          <Alert status="info" type={2}>
            Após realizar o pagamento, favor desconsiderar lembretes de cobrança da <b>Acordo Certo</b> ou do <b>Nubank</b>.
          </Alert>
        </div>
      </If>

      <>
        <Conditional
          condition={!isFirstInstallmentPaid}
          renderIf={
            <PaymentSection
              isBilletPollingActive={isPollingBillet}
              closedAgreement={closedAgreement}
              warningRef={warningRef}
              pixCode={pixCode}
              hasBillet={hasBillet}
              isPixExternal={isPixExternal}
              billet={billet}
              qrCodeUrl={qrCodeUrl}
              handleCopyPixCode={handleCopyPixCode}
              pixText={pixText}
              debtData={debtData}
              firstInstallmentExpDate={firstInstallmentExpDate}
              isFirstInstallmentPaid={isFirstInstallmentPaid}
              hasNewNubankDueDate={getNubankLoansMessage()}
              isLoadingCAPix={isLoadingCAPix}
            />
          }
        />

        <If condition={!!isItauDesenrola}>
          <div className="payment-data__alert">
            <Alert status="warning" type={2}>
              <b>Programa de Renegociação de Dívidas do Governo Federal 2026</b>
              <br />
              Seu contrato poderá ser renomeado de acordo com as regras do programa, mas não se preocupe, nada muda para você e você poderá acompanhar
              seu contrato nos canais internos do seu banco;
              <br />
              <br />
              <b>Cancelamento</b>
              <br />
              Sua renegociação não poderá ser cancelada após o pagamento da primeira parcela. Caso queira, você poderá quitá-la a qualquer momento,
              com desconto proporcional nos juros.
            </Alert>
          </div>
        </If>

        <If condition={isPixBillet}>
          <div className="payment-data__alert">
            <PixPaymentConfirmationAlert />
          </div>
        </If>

        <Conditional
          condition={showOrientationCard}
          renderIf={
            <OrientationCard
              hasDebitAccount={false}
              pix={isOnlyPix}
              hasNewNubankDueDate={getNubankLoansMessage()}
              isBradescoDesenrola={isBradescoDesenrola}
            />
          }
        />
        <Conditional
          condition={isFirstInstallmentPaid}
          renderIf={<CardSection firstInstallmentExpDate={firstInstallmentExpDate} debtData={debtData} />}
        />

        <Conditional
          condition={!isNCADCheckout && !isNeverEngaged && !isSimplified}
          renderIf={<UpdateContactData partnerLogo={debtData.display.partnerImage} />}
        />

        <HotjarSurvey surveyName="payment_view" />

        <Button className="payment-data__btn-details" expand="x" type="outlined" onClick={handleSeeAgreementDetails}>
          Ver detalhes do acordo
        </Button>
        <UpdateEmailModal isOpen={isModalOpen} onClose={handleModalClose} onSuccess={handleEmailUpdate} />
      </>
    </>
  );
};

export default PaymentData;
